import './painting-teach.css';

import { IonChip, IonIcon, IonLabel } from '@ionic/react';
import { ReactElement } from 'react';
import { IonItem } from '../../../../../../components/ionic/IonItem';
import { i18nKeyType } from '../../../../../../i18n';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { Platform, getPlatform } from '../../../../../../util/platform';
import { TeachItemPickerOptions } from '../../../../shared/component/teach/option/teach-item-picker-options';
import { TeachMoveOptions } from '../../../../shared/component/teach/option/teach-move-options';
import { TriggerOutputOptions } from '../../../../shared/component/teach/option/trigger-output-options';
import { WaitForOptions } from '../../../../shared/component/teach/option/wait-for-options';
import { TeachOptionProps } from '../../../../shared/component/teach/teach.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItemParameterMetadata,
  FlowItemRef,
} from '../../../../shared/model/flow.types';
import { BeaconPaintingData } from '../../model/beacon-painting-data';

const PaintingTeachOption = (props: TeachOptionProps): ReactElement => {
  const {
    icon,
    description,
    itemRef,
    isSelected,
    isOtherSelected,
    chipLabel,
    onAdd,
    onDone,
    onSelect,
    isDisabled,
  } = props;

  const platform = getPlatform();
  const isWeb = platform === Platform.WEB || platform === Platform.MOBILE_WEB;

  const { flowItemService } = useFlowServices();

  const requiredParameters: FlowItemParameterMetadata[] = [];
  if (itemRef) {
    requiredParameters.push(
      ...flowItemService.getRequiredParametersForTeaching(itemRef)
    );
  }

  const classList: string[] = ['option-item transparent-background'];
  if (isOtherSelected) {
    classList.push('dim');
  }
  if (isSelected) {
    classList.push('option-selected');
  }

  return (
    <div>
      <IonItem
        data-cy={`teach-option-${props.optionId?.replace(/ /g, '-')}`}
        data-cy-selected={isSelected}
        className={classList.join(' ')}
        lines="none"
        detail={false}
        onClick={(e) => {
          e.preventDefault();
          if (isSelected) {
            onDone();
          } else {
            onSelect();
          }
        }}
        {...(!isWeb && { href: '#' })}
      >
        <IonIcon icon={icon} slot="start" />
        <IonLabel>{getLocalizedText(description as i18nKeyType)}</IonLabel>
        {isSelected && (
          <IonChip
            className="add-button"
            slot="end"
            onClick={(e) => {
              e.preventDefault();
              onAdd();
            }}
            disabled={isDisabled ?? false}
          >
            {chipLabel || getLocalizedText('common:add')}
          </IonChip>
        )}
      </IonItem>
      {itemRef && (
        <FlowItemSubpanel itemRef={itemRef} show={isSelected} onAdd={onAdd} />
      )}
    </div>
  );
};

interface FlowItemSubpanelProps {
  itemRef: FlowItemRef;
  show: boolean;
  onAdd: () => void;
}

const FlowItemSubpanel = (
  props: FlowItemSubpanelProps
): ReactElement | null => {
  const { itemRef, onAdd, show } = props;

  if (show) {
    if (itemRef === BeaconPaintingData.FlowItemRef.PAINT) {
      return (
        <div className="sub-panel-overlay teach-panel-type-options">
          <TeachItemPickerOptions
            onAdd={onAdd}
            parameterMetadata={BeaconPaintingData.PaintTypeMetadata}
          />
        </div>
      );
    } else if (itemRef === BeaconPaintingData.FlowItemRef.MOVE) {
      return <TeachMoveOptions onAdd={onAdd} />;
    } else if (itemRef === BeaconPaintingData.FlowItemRef.WAIT_FOR) {
      return (
        <div className="sub-panel-overlay teach-panel-type-options">
          <WaitForOptions onAdd={onAdd} />
        </div>
      );
    } else if (itemRef === BeaconPaintingData.FlowItemRef.TRIGGER_OUTPUT) {
      return (
        <div className="sub-panel-overlay teach-panel-type-options">
          <TriggerOutputOptions onAdd={onAdd} />
        </div>
      );
    }
  }
  return null;
};

export { PaintingTeachOption };
